import { useCallback, useMemo } from 'react'
import { InteractionStatus, type AccountInfo, type RedirectRequest } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'

import { skipTenantCheck } from '../dev/flags'
import { loginScopes } from './msal-config'
import {
  extractClaims,
  isAuthorised,
  REQUIRED_ROLE,
  REQUIRED_TENANT,
  type MurmurationClaims,
} from './claims'
import { encodeReturnTo } from './use-return-to'

/**
 * Derived auth state surfaced to the UI.
 *
 * `signedIn`         : MSAL has an account in its in-memory cache.
 * `inProgress`       : MSAL is mid-redirect / silent-refresh. UI should
 *                      render a quiet "Loading..." rather than flicker
 *                      between sign-in and the requested page.
 * `claims`           : Typed view of the id_token claims (or null).
 * `authorised`       : `signedIn` AND tnt=suitsme AND roles includes bo.staff.
 *                      Defence-in-depth on top of the APIGW authoriser.
 * `signIn`           : Initiate the redirect login. Optional `returnTo`
 *                      same-origin path is round-tripped via OIDC `state`.
 * `signOut`          : Clear local MSAL cache + Entra session.
 */
export interface AuthState {
  readonly signedIn: boolean
  readonly inProgress: boolean
  readonly account: AccountInfo | null
  readonly claims: MurmurationClaims | null
  readonly authorised: boolean
  readonly hasRole: (role: string) => boolean
  readonly requiredRole: string
  readonly requiredTenant: string
  readonly signIn: (returnTo?: string) => Promise<void>
  readonly signOut: () => Promise<void>
}

/**
 * Hook the rest of the app uses for everything auth-related. Wraps
 * `useMsal()` + `useIsAuthenticated()` so callers never touch MSAL APIs
 * directly - that keeps the M2 issuer-swap (ADR-008) a config flip plus a
 * single seam to update.
 */
export function useAuth(): AuthState {
  const { instance, accounts, inProgress } = useMsal()
  const signedIn = useIsAuthenticated()

  const account = accounts[0] ?? null
  const claims = useMemo(() => extractClaims(account), [account])
  const authorised = useMemo(
    () => signedIn && isAuthorised(claims, { skipTenantCheck }),
    [signedIn, claims],
  )

  const signIn = useCallback(
    async (returnTo?: string) => {
      const request: RedirectRequest = {
        scopes: loginScopes,
        state: encodeReturnTo(returnTo),
        // Always force fresh interaction for the sign-in CTA. Silent refresh
        // is a separate flow handled by acquireTokenSilent on API call paths.
        prompt: 'select_account',
      }
      await instance.loginRedirect(request)
    },
    [instance],
  )

  const signOut = useCallback(async () => {
    // ADR-014 / `07a` 3a: sign-out is three things in sequence -
    //   1) clear in-memory MSAL cache (handled by logoutRedirect),
    //   2) hit Entra /logout with id_token_hint to end the Microsoft
    //      session cookie,
    //   3) land back on /sign-in via postLogoutRedirectUri.
    // Local-only sign-out is insufficient (the next sign-in would re-use
    // the persistent Entra session).
    const accountForHint = instance.getActiveAccount() ?? accounts[0] ?? null
    await instance.logoutRedirect({
      account: accountForHint,
      postLogoutRedirectUri: '/sign-in',
    })
  }, [instance, accounts])

  const hasRole = useCallback((role: string) => claims?.roles.includes(role) ?? false, [claims])

  return {
    signedIn,
    inProgress: inProgress !== InteractionStatus.None,
    account,
    claims,
    authorised,
    hasRole,
    requiredRole: REQUIRED_ROLE,
    requiredTenant: REQUIRED_TENANT,
    signIn,
    signOut,
  }
}
